import React, { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useController, useWatch } from 'react-hook-form';
import { InputNumber, Modal, Select } from 'antd';
import { UserOutlined, BankOutlined } from '@ant-design/icons';

import { 
  CreditRisk, 
  fixedRiskText,
  technologyTypeOptions,
  productionTypeOptions,
} from '@types';
import {
  AutoCompleteReferenceInput,
  IGrid,
  InitialCard,
  InitialRow,
  SelectInput,
} from '@components';
import { dateFormatter } from '@utils';
import {
  FinancialStatement,
  isFinancialStatementAllDone,
} from '@pages/credit-risk/analysis/components/FinancialStatement';

const MAX_AMOUNT_VALUE = 1000000000;

const termsOptions = [
  {
    id: '3 Meses',
    name: '3 Meses',
  },
  {
    id: '4 Meses',
    name: '4 Meses',
  },
  {
    id: '5 Meses',
    name: '5 Meses',
  },
  {
    id: '6 Meses',
    name: '6 Meses',
  },
  {
    id: '7 Meses',
    name: '7 Meses',
  },
  {
    id: '8 Meses',
    name: '8 Meses',
  },
  {
    id: '9 Meses',
    name: '9 Meses',
  },
  {
    id: '10 Meses',
    name: '10 Meses',
  },
  {
    id: '11 Meses',
    name: '11 Meses',
  },
  {
    id: '12 Meses',
    name: '12 Meses',
  },
];

export const OkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="9" fill="#73BA47"/>
    <path d="M4.23233 10.0457C3.92256 9.7443 3.92256 9.2557 4.23233 8.95434C4.54211 8.65298 5.04436 8.65298 5.35414 8.95434L8.15865 11.6827C8.46842 11.984 8.46842 12.4726 8.15865 12.774C7.84887 13.0753 7.34662 13.0753 7.03684 12.774L4.23233 10.0457Z" fill="white"/>
    <path d="M12.6459 6.22602C12.9556 5.92466 13.4579 5.92466 13.7677 6.22602C14.0774 6.52738 14.0774 7.01599 13.7677 7.31735L8.15865 12.774C7.84887 13.0753 7.34662 13.0753 7.03684 12.774C6.72707 12.4726 6.72707 11.984 7.03684 11.6827L12.6459 6.22602Z" fill="white"/>
  </svg>
);

export const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="9" fill="#D32F2F"/>
    <path d="M11.8284 4.75725C12.219 4.36672 12.8521 4.36672 13.2427 4.75725C13.6332 5.14777 13.6332 5.78094 13.2427 6.17146L6.17158 13.2425C5.78106 13.6331 5.1479 13.6331 4.75737 13.2425C4.36685 12.852 4.36685 12.2188 4.75737 11.8283L11.8284 4.75725Z" fill="white"/>
    <path d="M11.8284 4.75725C12.219 4.36672 12.8521 4.36672 13.2427 4.75725C13.6332 5.14777 13.6332 5.78094 13.2427 6.17146L6.17158 13.2425C5.78106 13.6331 5.1479 13.6331 4.75737 13.2425C4.36685 12.852 4.36685 12.2188 4.75737 11.8283L11.8284 4.75725Z" fill="white"/>
    <path d="M13.2427 11.8283C13.6332 12.2188 13.6332 12.852 13.2427 13.2425C12.8521 13.6331 12.219 13.6331 11.8284 13.2425L4.75737 6.17146C4.36685 5.78094 4.36685 5.14777 4.75737 4.75725C5.1479 4.36672 5.78106 4.36672 6.17158 4.75725L13.2427 11.8283Z" fill="white"/>
    <path d="M13.2427 11.8283C13.6332 12.2188 13.6332 12.852 13.2427 13.2425C12.8521 13.6331 12.219 13.6331 11.8284 13.2425L4.75737 6.17146C4.36685 5.78094 4.36685 5.14777 4.75737 4.75725C5.1479 4.36672 5.78106 4.36672 6.17158 4.75725L13.2427 11.8283Z" fill="white"/>
  </svg>
);

interface Props {
  risk: CreditRisk;
  isFixed: boolean;
  labelGrid?: IGrid;
  valueGrid?: IGrid;
}

export const RiskGeneral = ({ risk, isFixed, labelGrid, valueGrid }: Props) => {
  const { credit, variety } = risk;
  const { user } = credit;

  const [showFinancialStatementModal, setShowFinancialStatementModal] = useState<boolean>(false);
  const [savedFinancialStatement, setSavedFinancialStatement] = useState<any>(null);

  useEffect(() => {
    setSavedFinancialStatement(null);
  }, [risk?.id]);

  const {
    field: { value: requestAmount, onChange: onRequestAmountChange },
  } = useController({ name: 'requestAmount' });
  const {
    field: { value: interestRate, onChange: onInterestRateChange },
  } = useController({ name: 'interestRate' });
  const {
    field: { value: moratoryRate, onChange: onMoratoryRateChange },
  } = useController({ name: 'moratoryRate' });
  const {
    field: { value: approvedAmount, onChange: onApprovedAmountChange },
  } = useController({ name: 'approvedAmount' });
  const {
    field: { value: creditTerm, onChange: onCreditTermChange },
  } = useController({ name: 'creditTerm' });

  const {
    field: { value: membership, onChange: onMembershipChange },
  } = useController({ name: 'membership' });

  const {
    field: { value: varietyValue, onChange: onVarietyChange },
  } = useController({ name: 'variety' });

  const handleRequestAmountChange = (value: number | null) => {
    if (value && value > MAX_AMOUNT_VALUE) return;
    
    const newValue = (value || 0) >= MAX_AMOUNT_VALUE ? MAX_AMOUNT_VALUE : value;

    onRequestAmountChange(newValue);
  };

  const handleInterestRateChange = (value: number | null) => {
    onInterestRateChange(value);
  };

  const handleMoratoryRateChange = (value: number | null) => {
    onMoratoryRateChange(value);
  };

  const handleApprovedAmountChange = (value: number | null) => {
    if (value && value > MAX_AMOUNT_VALUE) return;

    const newValue = (value || 0) >= MAX_AMOUNT_VALUE ? MAX_AMOUNT_VALUE : value;

    onApprovedAmountChange(newValue);
  };

  const handleMembershipChange = (value: number | null) => {
    onMembershipChange(value);
  };

  const replyTerms = useWatch({ name: 'replyTerms' });

  useEffect(() => {
    if (replyTerms && Array.isArray(replyTerms)) {
      const creditTermReply = replyTerms.find((t: any) => t.tag === 'creditTerm');

      if (creditTermReply?.value && !creditTerm) {
        onCreditTermChange(creditTermReply.value);
      }
    }
  }, [replyTerms]);

  const firstCrop = credit.creditCrops[0]?.financedCrop?.nameMx || '-';

  const getVarietyOptions = () => {
    const firstCropData = credit.creditCrops[0]?.financedCrop;
    if (!firstCropData?.varieties || firstCropData.varieties.length === 0) return [];
    
    return firstCropData.varieties.map((v: any) => {
      const varietyName = typeof v === 'string' ? v : v.variety;
      return {
        label: varietyName,
        value: varietyName,
      };
    });
  };

  const varietyOptions = getVarietyOptions();

  const varietyArray = varietyValue 
    ? varietyValue.split(',').map((v: string) => v.trim()).filter(Boolean)
    : [];

  const handleVarietyChange = (values: string[]) => {
    const varietyString = values.join(', ');
    onVarietyChange(varietyString);
  };

  const cls = useStyles();

  return (
    <InitialCard left="General information" autoHeight extraPadding
      lockText={risk.isFixed ? fixedRiskText : null}>
      <div className={cls.head}>
        {risk?.credit?.user?.isCompany && (
          <div className={cls.financialAction} onClick={() => setShowFinancialStatementModal(true)}>
            {isFinancialStatementAllDone(savedFinancialStatement ?? risk?.financialStatement) ? (
              <OkIcon />
            ) : (
              <ErrorIcon />
            )}{' '}
            Review Financial Statements
          </div>
        )}
      </div>
      <div className={cls.box}>
        <InitialRow
          title="ID Verqor"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{credit.code}</InitialRow>
        <InitialRow
          title="Client"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{credit.accreditedName}</InitialRow>
        
        <InitialRow
          title="RFC"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{user.rfc || user.rfcActual || '-'}</InitialRow>

        <InitialRow title="Withholding Company:" labelGrid={labelGrid} valueGrid={valueGrid}>
          <AutoCompleteReferenceInput
            source="mainBuyerName"
            reference="/dealer/by-type/buyer"
            placeholder="Search Buyers"
          />
        </InitialRow>

        <InitialRow
          title="Manager"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{credit.user?.responsibleAdmin?.name || '-'}</InitialRow>

        <InitialRow
          title="Owner"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{credit.user?.contactOwner?.name || '-'}</InitialRow>

        <InitialRow
          title="Channel"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{user.sourceEn}</InitialRow>

        <InitialRow
          title="State"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          {user.state?.name}
        </InitialRow>

        <InitialRow
          title="Crop"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          {firstCrop}
        </InitialRow>
        
        <InitialRow
          title="Variety"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <Select
            mode="tags"
            style={{ width: '100%' }}
            value={varietyArray}
            onChange={handleVarietyChange}
            options={varietyOptions}
            className={cls.varietySelect}
            tokenSeparators={[',']}
          />
        </InitialRow>
        
        <InitialRow
          title="Technology type"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <SelectInput
            source="technologyType"
            label={false}
            choices={technologyTypeOptions}
            className={cls.field}
          />
        </InitialRow>
        
        <InitialRow
          title="Production Type"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <SelectInput
            source="productionType"
            label={false}
            choices={productionTypeOptions}
            className={cls.field}
          />
        </InitialRow>
        
        <InitialRow
          title="Requested Amount"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
          className={cls.requested}
        >
          <div className={cls.amount}>
            <InputNumber
              min={0}
              max={1000000000}
              value={requestAmount || 0}
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => +(value?.replace(/\$\s?|(,*)/g, '') || 0)}
              onChange={handleRequestAmountChange}
              bordered={false}
              className={cls.fieldNumber}
              controls={false}
            />
          </div>
        </InitialRow>
        <InitialRow
          title="Sent to Analysis"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{dateFormatter.toDateSlash(credit.createdAt)}</InitialRow>
        
        <InitialRow
          title="Completed Analysis"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >{risk.completedAnalysisAt ? dateFormatter.toDateSlash(risk.completedAnalysisAt) : '-'}</InitialRow>

        <div className={cls.sectionTitle}>Upon Credit Approval</div>

        <InitialRow
          title="Interest Rate (Inc. IVA)"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            precision={2}
            value={interestRate || 0}
            onChange={handleInterestRateChange}
            formatter={(value) => `${value} %`}
            parser={(value) => {
              const match = value?.match(/^(\d+\.?\d*)\s*%?/);
              return match ? parseFloat(match[1]) : 0;
            }}
            bordered={false}
            className={cls.fieldNumber}
            controls={false}
          />
        </InitialRow>

        <InitialRow
          title="Moratory Rate (Inc. IVA)"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            precision={2}
            value={moratoryRate || 0}
            onChange={handleMoratoryRateChange}
            formatter={(value) => `${value} %`}
            parser={(value) => {
              const match = value?.match(/^(\d+\.?\d*)\s*%?/);
              return match ? parseFloat(match[1]) : 0;
            }}
            bordered={false}
            className={cls.fieldNumber}
            controls={false}
          />
        </InitialRow>

        <InitialRow
          title="Approved Amount"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <InputNumber
            min={0}
            max={1000000000}
            value={approvedAmount || 0}
            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => +(value?.replace(/\$\s?|(,*)/g, '') || 0)}
            onChange={handleApprovedAmountChange}
            bordered={false}
            className={cls.fieldNumber}
            controls={false}
          />
        </InitialRow>

        <InitialRow
          title="Credit Term"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <SelectInput
            source="creditTerm"
            label={false}
            choices={termsOptions}
            className={cls.field}
          />
        </InitialRow>

        <InitialRow
          title="Verqor Membership"
          labelGrid={labelGrid}
          valueGrid={valueGrid}
        >
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            precision={2}
            value={membership || 0}
            onChange={handleMembershipChange}
            formatter={(value) => `${value} %`}
            parser={(value) => {
              const match = value?.match(/^(\d+\.?\d*)\s*%?/);
              return match ? parseFloat(match[1]) : 0;
            }}
            bordered={false}
            className={cls.fieldNumber}
            controls={false}
          />
        </InitialRow>

        <div className={cls.clientType}>
          <ClientTypeIndicator isCompany={user.isCompany} />
        </div>
      </div>

      <Modal
        open={showFinancialStatementModal}
        onCancel={() => setShowFinancialStatementModal(false)}
        className={cls.financialStatementModal}
        footer={null}
      >
        <FinancialStatement
          riskId={risk.id}
          financialStatement={risk?.financialStatement}
          close={() => setShowFinancialStatementModal(false)}
          onSave={setSavedFinancialStatement}
        />
      </Modal>
    </InitialCard>
  );
};

const ClientTypeIndicator = ({ isCompany }: {isCompany: boolean}) => {
  const cls = useStyles();
  const Icon = isCompany ? BankOutlined : UserOutlined;
  const text = isCompany ? 'PM - Persona Moral' : 'PF - Persona Física';

  return (
    <div className={cls.clientTypeIndicator}>
      <Icon className={cls.clientTypeIcon} />
      <div className={cls.clientTypeText}>{text}</div>
    </div>
  );
};

const useStyles = makeStyles({
  clientType: {
    display: 'none',
    marginTop: 16,
  },
  clientTypeIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    borderRadius: 8,
    border: '1px solid var(--primary-color)',
    background: '#F5F7FA',
  },
  clientTypeIcon: {
    fontSize: 20,
    color: 'var(--primary-color)',
  },
  clientTypeText: {
    color: 'var(--primary-color)',
    fontSize: 14,
    fontWeight: 500,
  },
  box: {
    marginTop: 15,
    fontFamily: 'var(--font-primary)',
    color: 'var(--text-color)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,

    '& > .ant-row': {
      marginTop: 0,
    },

    '& > div': {
      minHeight: 32,
    },
  },
  requested: {
    marginTop: '12px !important',
  },
  amount: {
    '& p': {
      display: 'none',
    },
  },
  amountValue: {
    fontSize: 12,
    color: '#5b5b5b',
  },
  error: {
    color: 'red',
  },
  field: {
    height: 32,
    marginTop: '0px !important',
    width: '100%',

    '& input': {
      fontSize: 18,
      padding: '0px !important',
    },

    '& div': {
      marginTop: 0,
    },

    '& p': {
      display: 'none',
    },
  },
  fieldNumber: {
    height: 32,
    marginTop: '0px !important',
    width: '100%',
    borderBottom: '1px solid rgba(0, 42, 119, 0.10)',
    
    '& div': {
      marginTop: 0,
    },

    '&:hover': {
      borderBottom: '1px solid rgba(0, 42, 119, 0.10)',
    },

    '& input': {
      fontSize: 18,
      padding: '0px !important',
    },
  },
  '.MuiInputBase-formControl': {
    marginTop: '0px !important',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: 'var(--title-color)',
  },
  varietySelect: {
    flex: 1,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    
    '& .ant-select-selection-overflow': {
      display: 'flex',
      flexWrap: 'wrap',
      borderBottom: '1px solid #e4e8f2 !important',
    },
    
    '& .ant-select-selector': {
      padding: '0 !important',
      border: 'none !important',
      borderRadius: '0 !important',
      boxShadow: 'none !important',
      maxHeight: '120px !important',
      overflowY: 'auto !important',
      overflowX: 'hidden !important',
    },

    '& .ant-select-selector:focus, & .ant-select-selector:hover, &.ant-select-focused .ant-select-selector': {
      border: 'none !important',
      boxShadow: 'none !important',
    },
  },
  head: {
    position: 'absolute',
    top: 0,
    right: 12,
    padding: 20,
  },
  financialAction: {
    background: '#042E6B',
    borderRadius: 8,
    fontWeight: 700,
    color: '#fff',
    padding: '10px 16px',
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  financialStatementModal: {
    width: '1120px !important',
  },
});
